import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClipboardCheck, ArrowLeft, Check, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const loadIdentity = () => {
  try { return JSON.parse(localStorage.getItem("sa_di_identity") || "null") || { name: "", pin: "" }; } catch { return { name: "", pin: "" }; }
};

export default function DailyInspectionScan({ tail, iid, onExit }) {
  const [doc, setDoc] = useState(null);
  const [identity, setIdentity] = useState(loadIdentity);
  const [ready, setReady] = useState(() => !!(loadIdentity().name && /^\d{4}$/.test(loadIdentity().pin)));
  const [header, setHeader] = useState({ date: "", af_hours: "", af_cycles: "", jl_number: "", oil1: "", oil2: "", nose1: "", nose2: "", lh1: "", lh2: "", rh1: "", rh2: "" });
  const [fieldVals, setFieldVals] = useState({});
  const [busyNo, setBusyNo] = useState(null);
  const [savingHeader, setSavingHeader] = useState(false);
  const [completing, setCompleting] = useState(false);
  const headerSynced = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/maintenance/public/daily-inspections/${iid}`);
      setDoc(r.data);
      if (!headerSynced.current) {
        headerSynced.current = true;
        setHeader({
          date: r.data.date || "",
          af_hours: r.data.af_hours || "",
          af_cycles: r.data.af_cycles || "",
          jl_number: r.data.jl_number || "",
          oil1: (r.data.oil_added || {})["1"] || "",
          oil2: (r.data.oil_added || {})["2"] || "",
          nose1: (r.data.tire_pressures || {}).nose1 || "",
          nose2: (r.data.tire_pressures || {}).nose2 || "",
          lh1: (r.data.tire_pressures || {}).lh1 || "",
          lh2: (r.data.tire_pressures || {}).lh2 || "",
          rh1: (r.data.tire_pressures || {}).rh1 || "",
          rh2: (r.data.tire_pressures || {}).rh2 || "",
        });
        setFieldVals(r.data.field_values || {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inspection not found");
      onExit();
    }
  }, [iid, onExit]);
  useEffect(() => { load(); }, [load]);

  // Live multi-user sync — refetch on any maintenance ping for this tail.
  useEffect(() => {
    let ws;
    let closed = false;
    const connect = () => {
      try {
        ws = new WebSocket(`${API_BASE.replace(/^http/, "ws")}/ws/maintenance`);
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (!msg.tail || msg.tail === tail) load();
          } catch { /* ignore */ }
        };
        ws.onclose = () => { if (!closed) setTimeout(connect, 3000); };
      } catch { /* ignore */ }
    };
    connect();
    const iv = setInterval(load, 30000);
    return () => { closed = true; clearInterval(iv); try { ws && ws.close(); } catch { /* ignore */ } };
  }, [tail, load]);

  const saveIdentity = () => {
    if (!identity.name.trim()) { toast.error("Your name is required"); return; }
    if (!/^\d{4}$/.test(identity.pin)) { toast.error("Enter the 4-digit PIN"); return; }
    try { localStorage.setItem("sa_di_identity", JSON.stringify({ name: identity.name.trim(), pin: identity.pin })); } catch { /* ignore */ }
    setReady(true);
  };

  const creds = () => ({ name: identity.name.trim(), pin: identity.pin });

  const toggleSign = async (it, na = false) => {
    if (doc.status !== "in_progress") return;
    setBusyNo(it.no);
    try {
      const r = await axios.post(`${API_BASE}/maintenance/public/daily-inspections/${iid}/sign`, { ...creds(), no: it.no, na });
      setDoc(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sign");
      if (e?.response?.status === 403 && /PIN/i.test(e?.response?.data?.detail || "")) setReady(false);
    } finally { setBusyNo(null); }
  };

  const saveHeader = async () => {
    setSavingHeader(true);
    try {
      const r = await axios.patch(`${API_BASE}/maintenance/public/daily-inspections/${iid}/header`, {
        ...creds(),
        date: header.date,
        af_hours: header.af_hours,
        af_cycles: header.af_cycles,
        jl_number: header.jl_number,
        oil_added: { 1: header.oil1, 2: header.oil2 },
        tire_pressures: { nose1: header.nose1, nose2: header.nose2, lh1: header.lh1, lh2: header.lh2, rh1: header.rh1, rh2: header.rh2 },
        field_values: fieldVals,
      });
      setDoc(r.data);
      toast.success("Form details saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSavingHeader(false); }
  };

  const complete = async () => {
    if (!window.confirm("Complete this Daily Inspection? It becomes read-only and appears on the Maintenance page.")) return;
    setCompleting(true);
    try {
      const r = await axios.post(`${API_BASE}/maintenance/public/daily-inspections/${iid}/complete`, creds());
      setDoc(r.data);
      toast.success("Daily Inspection completed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to complete"); }
    finally { setCompleting(false); }
  };

  if (!doc) return <div className="min-h-screen bg-summit-canvas flex items-center justify-center text-sm text-gray-400">Loading…</div>;

  const items = doc.items || [];
  const signed = items.filter((i) => i.signed_at).length;
  const inProgress = doc.status === "in_progress";
  const isATR = (doc.fleet || "ATR72").toUpperCase() === "ATR72";

  return (
    <div className="min-h-screen bg-summit-canvas px-4 py-6">
      <div className="max-w-2xl mx-auto">
        <button className="inline-flex items-center gap-1.5 text-xs text-summit-primary hover:underline mb-3" onClick={onExit} data-testid="di-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {tail}
        </button>

        <div className="bg-white border border-summit-hairline rounded-lg overflow-hidden" data-testid="di-card">
          <div className="bg-summit-primary text-white px-4 py-3 flex items-center gap-2.5 flex-wrap">
            <ClipboardCheck className="w-4 h-4 shrink-0" />
            <span className="font-display font-semibold">{doc.form_title || (isATR ? "ATR Daily Inspection" : "D228 Daily Inspection")}</span>
            <span className="font-mono text-[10px] opacity-80">{doc.form_number}</span>
            <span className={`ml-auto px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${inProgress ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid="di-status">
              {inProgress ? `${signed}/${items.length} signed` : "Complete"}
            </span>
          </div>

          {!ready && inProgress && (
            <div className="p-4 border-b border-summit-hairline space-y-3 bg-amber-50/50" data-testid="di-identity-gate">
              <p className="text-xs text-gray-600">Enter your name and the aircraft&apos;s 4-digit QR PIN once — your initials are stamped on every item you sign.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">Your name *</Label>
                  <Input className="mt-1 h-9" value={identity.name} onChange={(e) => setIdentity((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. John Smith" maxLength={60} data-testid="di-name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">4-digit PIN *</Label>
                  <Input className="mt-1 h-9 w-28 font-mono tracking-[0.3em] text-center" inputMode="numeric" maxLength={4} value={identity.pin} onChange={(e) => setIdentity((s) => ({ ...s, pin: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="••••" data-testid="di-pin" />
                </div>
              </div>
              <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={saveIdentity} data-testid="di-identity-save">
                Start signing
              </Button>
            </div>
          )}
          {ready && inProgress && (
            <div className="px-4 py-2 border-b border-summit-hairline flex items-center gap-2 text-xs text-gray-600 bg-summit-glacier-soft/40" data-testid="di-identity-bar">
              Signing as <b>{identity.name}</b>
              <button className="ml-auto text-summit-primary hover:underline" onClick={() => setReady(false)} data-testid="di-identity-change">Change</button>
            </div>
          )}

          <div className="p-4 border-b border-summit-hairline">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Form details {inProgress ? "(required to complete)" : ""}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Date *</Label>
                <Input type="date" className="mt-1 h-9 text-xs" value={header.date} disabled={!inProgress} onChange={(e) => setHeader((h) => ({ ...h, date: e.target.value }))} data-testid="di-hdr-date" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">A/F Hours *</Label>
                <Input className="mt-1 h-9 text-xs" value={header.af_hours} disabled={!inProgress} onChange={(e) => setHeader((h) => ({ ...h, af_hours: e.target.value }))} data-testid="di-hdr-hours" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">A/F Cycles *</Label>
                <Input className="mt-1 h-9 text-xs" value={header.af_cycles} disabled={!inProgress} onChange={(e) => setHeader((h) => ({ ...h, af_cycles: e.target.value }))} data-testid="di-hdr-cycles" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">JL # *</Label>
                <Input className="mt-1 h-9 text-xs" value={header.jl_number} disabled={!inProgress} onChange={(e) => setHeader((h) => ({ ...h, jl_number: e.target.value }))} data-testid="di-hdr-jl" />
              </div>
              {isATR && (
                <>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-gray-500">Oil added #1 (qt)</Label>
                    <Input className="mt-1 h-9 text-xs" value={header.oil1} disabled={!inProgress} onChange={(e) => setHeader((h) => ({ ...h, oil1: e.target.value }))} data-testid="di-hdr-oil1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-gray-500">Oil added #2 (qt)</Label>
                    <Input className="mt-1 h-9 text-xs" value={header.oil2} disabled={!inProgress} onChange={(e) => setHeader((h) => ({ ...h, oil2: e.target.value }))} data-testid="di-hdr-oil2" />
                  </div>
                </>
              )}
            </div>
            <div className="text-[11px] text-gray-500 mt-2">Registration: <b>{doc.tail}</b> (auto)</div>
            {inProgress && ready && (
              <Button size="sm" variant="outline" className="mt-2 h-8 text-xs" onClick={saveHeader} disabled={savingHeader} data-testid="di-hdr-save">
                {savingHeader ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />} Save details
              </Button>
            )}
          </div>

          <div className="divide-y divide-summit-hairline" data-testid="di-items">
            {items.map((it) => (
              <div key={it.no} className={`px-4 py-3 flex items-start gap-3 ${it.signed_at ? "bg-emerald-50/50" : ""}`} data-testid={`di-item-${it.no}`}>
                <div className="w-6 shrink-0 font-mono text-[11px] text-gray-400 pt-0.5">{it.no}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs whitespace-pre-wrap leading-snug">{it.task}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1 font-mono text-[9px] uppercase tracking-widest text-gray-400">
                    <span>{it.item}</span>
                    {it.ata && <span>· ATA {it.ata}</span>}
                    {it.zone && <span>· Zone {it.zone}</span>}
                    {it.action && <span>· {it.action}</span>}
                  </div>
                  {(it.fields || []).length > 0 && (
                    <div className="mt-2 rounded-md border border-summit-hairline bg-summit-glacier-soft/30 p-2" data-testid={`di-fields-${it.no}`}>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mb-1.5">Readings — required to complete</div>
                      <div className="grid grid-cols-2 gap-2">
                        {it.fields.map((f) => (
                          <div key={f.key}>
                            <Label className="text-[9px] uppercase tracking-widest text-gray-500">{f.label} *</Label>
                            <div className="flex gap-1 mt-0.5">
                              <Input className="h-8 text-xs" value={(fieldVals[it.no] || {})[f.key] || ""} disabled={!inProgress} onChange={(e) => setFieldVals((s) => ({ ...s, [it.no]: { ...(s[it.no] || {}), [f.key]: e.target.value } }))} data-testid={`di-field-${it.no}-${f.key}`} />
                              {inProgress && (
                                <button type="button" className="h-8 px-1.5 rounded-md border border-dashed border-summit-hairline font-mono text-[9px] uppercase tracking-widest text-gray-400 hover:text-amber-700 hover:border-amber-300" onClick={() => setFieldVals((s) => ({ ...s, [it.no]: { ...(s[it.no] || {}), [f.key]: "N/A" } }))} title="Not available" data-testid={`di-field-na-${it.no}-${f.key}`}>N/A</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {inProgress && ready && (
                        <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]" onClick={saveHeader} disabled={savingHeader} data-testid={`di-fields-save-${it.no}`}>
                          {savingHeader ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save readings
                        </Button>
                      )}
                    </div>
                  )}
                  {isATR && it.no === 14 && (
                    <div className="mt-2 rounded-md border border-summit-hairline bg-summit-glacier-soft/30 p-2" data-testid="di-tires">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mb-1.5">Tire pressures — required to complete</div>
                      <div className="grid grid-cols-2 gap-2">
                        {[["nose1", "Nose #1"], ["nose2", "Nose #2"], ["lh1", "LH Main #1"], ["lh2", "LH Main #2"], ["rh1", "RH Main #1"], ["rh2", "RH Main #2"]].map(([k, label]) => (
                          <div key={k}>
                            <Label className="text-[9px] uppercase tracking-widest text-gray-500">{label} *</Label>
                            <div className="flex gap-1 mt-0.5">
                              <Input className="h-8 text-xs" value={header[k]} disabled={!inProgress} onChange={(e) => setHeader((h) => ({ ...h, [k]: e.target.value }))} data-testid={`di-tire-${k}`} />
                              {inProgress && (
                                <button type="button" className="h-8 px-1.5 rounded-md border border-dashed border-summit-hairline font-mono text-[9px] uppercase tracking-widest text-gray-400 hover:text-amber-700 hover:border-amber-300" onClick={() => setHeader((h) => ({ ...h, [k]: "N/A" }))} title="Not available" data-testid={`di-tire-na-${k}`}>N/A</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {inProgress && ready && (
                        <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]" onClick={saveHeader} disabled={savingHeader} data-testid="di-tire-save">
                          {savingHeader ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save tire pressures
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="shrink-0 w-24 text-center">
                  {it.signed_at ? (
                    <button
                      className={`w-full rounded-md border px-2 py-1.5 disabled:opacity-70 ${it.sign_na ? "border-amber-300 bg-amber-100" : "border-emerald-300 bg-emerald-100"}`}
                      onClick={() => toggleSign(it)}
                      disabled={!inProgress || !ready || busyNo === it.no}
                      title={inProgress ? "Tap to un-sign (only your own signature)" : undefined}
                      data-testid={`di-sign-${it.no}`}
                    >
                      <div className={`font-display font-bold text-sm ${it.sign_na ? "text-amber-800" : "text-emerald-800"}`}>{it.sign_initials}</div>
                      <div className={`text-[9px] truncate ${it.sign_na ? "text-amber-700" : "text-emerald-700"}`}>{it.sign_name}</div>
                      <div className={`text-[9px] ${it.sign_na ? "text-amber-600" : "text-emerald-600"}`}>{fmtWhen(it.signed_at)}</div>
                    </button>
                  ) : (
                    <div className="space-y-1">
                      <Button
                        size="sm"
                        className="w-full h-9 bg-summit-primary hover:bg-summit-primary-soft text-white"
                        onClick={() => toggleSign(it)}
                        disabled={!inProgress || !ready || busyNo === it.no}
                        data-testid={`di-sign-${it.no}`}
                      >
                        {busyNo === it.no ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Done</>}
                      </Button>
                      <button
                        className="w-full rounded-md border border-dashed border-summit-hairline py-1 font-mono text-[10px] uppercase tracking-widest text-gray-400 hover:text-amber-700 hover:border-amber-300 disabled:opacity-50"
                        onClick={() => toggleSign(it, true)}
                        disabled={!inProgress || !ready || busyNo === it.no}
                        title="Mark not available / not applicable"
                        data-testid={`di-na-${it.no}`}
                      >
                        N/A
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-summit-hairline">
            {inProgress ? (
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={complete} disabled={!ready || completing || signed < items.length} data-testid="di-complete-btn">
                {completing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-1.5" />}
                {signed < items.length ? `Complete inspection (${items.length - signed} left)` : "Complete inspection"}
              </Button>
            ) : (
              <p className="text-xs text-emerald-700 text-center" data-testid="di-complete-note">
                Completed by {doc.completed_by_name} · {fmtWhen(doc.completed_at)} — printable from the Maintenance page.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
